import { eventBus } from '../eventBus.js';
import { array } from '../../node_modules/@lfi/jsonstat-js/dist/array.js';

/**
 * Dynamically creates a (nested) list of selectable items
 *
 * @namespace dataSelector
 *
 * @property {JsonStatReader} _reader - Instance of the JsonStatReader used to read and parse JSON-Stat data.
 * @property {string} _targetDivId - String identifier for the target div where the data selector UI is rendered. (private)
 * @property {HTMLDivElement} _targetDivElement - Reference to the DOM element of the target div. (private)
 */
export const dataSelector = {

  /**
   * @type {JsonStatReader}
   */
  _reader: null,

  /**
   * ID of the div where the layer selector gets inserted
   * @type String
   * @private
   */
  _targetDivId: 'data-selector',

  /**
   * DOM element of the div where the layer selector gets inserted.
   * @type HTMLDivElement
   * @private
   */
  _targetDivElement: null,

  /**
   * Initialize the module.
   * @param {JsonStatReader} jsonStatReader
   * @param {Number} rowIdx
   * @returns {void}
   */
  init (jsonStatReader, rowIdx) {
    this._reader = jsonStatReader;
    this._targetDivElement = document.getElementById(this._targetDivId);
    this._targetDivElement.append(this._createTitle(), this.createItems(rowIdx));
  },

  /**
   * Create the title of the list.
   * @returns {HTMLDivElement} title
   * @private
   */
  _createTitle () {
    let ul, li, previousLi;
    const titleDiv = document.createElement('div');
    titleDiv.classList.add('data-selector__title');

    const dimensions = this._reader.getDimIdOthers();
    dimensions.forEach((dimId, i) => {
      ul = document.createElement('ul');
      li = document.createElement('li');
      li.innerText = this._reader.getLabel(dimId);
      ul.append(li);
      if (i === 0) {
        titleDiv.append(ul);
      } else {
        previousLi.append(ul);
      }
      previousLi = li;
    });
    return titleDiv;
  },

  /**
   * Create the data selector list,
   * Creates an unordered list to select the data series displayed on the map.
   * TODO: refactor (method is to large)
   * TODO: merge two cases (countKlassifizierungsmerkmale === ?) and possibly for n Klassifizierungsmerkmale?
   * TODO: use recursion
   * @returns {HTMLDivElement} list of selectable layers
   * @param {Number} rowIdx current rowIdx
   * @private
   */
  createItems (rowIdx) {
    let idx = 0;
    const shape = this._reader.getDimSizes();
    const selectorDiv = document.createElement('div');
    selectorDiv.classList.add('panel__content-container', 'data-selector__content');

    const dimensions = this._reader.getDimIdOthers();
    const outerUl = document.createElement('ul');
    outerUl.classList.add((dimensions.length === 1) ? 'tree' : 'nested');

    if (dimensions.length === 1) {
      const dimId = dimensions[0];
      this._reader.jsonStat.dimension[dimId].category.index.forEach(catId => {
        const offset = array.rowToLinear(idx, shape)[0];
        const li = document.createElement('li');
        if (this._reader.isNullrow(offset)) {
          li.classList.add('nullrow');
        }
        const label = this._reader.getCategoryLabel(dimId, catId);
        const a = this._createLink(label, idx, rowIdx);
        li.append(a);
        outerUl.append(li);
        idx++;
      });
    } else if (dimensions.length === 2) {
      const dimId = dimensions[0];
      this._reader.jsonStat.dimension[dimId].category.index.forEach(outerLabelIndex => {
        const li = document.createElement('li');
        li.innerHTML = '<span>' + this._reader.getCategoryLabel(dimId, outerLabelIndex) + '</span>';
        outerUl.append(li);
        const innerUl = document.createElement('ul');
        innerUl.classList.add('tree');
        let prop = this._reader.jsonStat.dimension[dimensions[1]].category.index;
        prop.forEach((innerLabelIndex, i) => {
          const label = this._reader.getCategoryLabel(dimensions[1], innerLabelIndex);
          const li = document.createElement('li');
          const offset = array.rowToLinear(idx, shape)[0];
          if (this._reader.isNullrow(offset)) {
            li.classList.add('nullrow');
          }
          const a = this._createLink(label, idx, rowIdx);
          li.append(a);
          if (prop.length === i + 1) {
            li.classList.add('last');
          }
          innerUl.append(li);
          idx++;
        });
        li.append(innerUl);
        if (innerUl.querySelectorAll('li.nullrow').length === prop.length) {
          li.classList.add('nullrow');
        }
      });
    }
    selectorDiv.append(outerUl);
    return selectorDiv;
  },

  /**
   * Create the link for a layer and attach a click event.
   * @param {String} label
   * @param {Number} idx row index
   * @param {Number} rowIdx current row index
   * @returns {HTMLAnchorElement}
   * @private
   */
  _createLink (label, idx, rowIdx) {
    const a = document.createElement('a');
    a.innerText = label;
    a.dataset['rowIdx'] = idx;
    if (idx === rowIdx) {
      a.classList.add('active');
    }
    a.addEventListener('click', this._handleClick.bind(this));
    return a;
  },

  /**
   * Handle the clicked event.
   * Handles the click on the link by setting the rowIdx and dispatching the custom event RowSelectEvent.
   * @param {MouseEvent} evt
   * @emits eventBus~rowSelect
   * @returns {void}
   * @private
   */
  _handleClick (evt) {
    evt.preventDefault();
    const rowIdx = Number(evt.target.dataset['rowIdx']);
    this.highlightRow(rowIdx);
    eventBus.dispatch('rowSelect', {
      rowIdx: rowIdx
    });
  },

  /**
   * Highlight the current active link.
   * @returns {void}
   * @private
   * @param {number} rowIdx row index
   */
  highlightRow (rowIdx) {
    let link;

    link = document.querySelector('.data-selector__content a.active');
    link.classList.remove('active');
    link = document.querySelector('.data-selector__content a[data-row-idx="' + rowIdx + '"]');
    link.classList.add('active');
  },

  /**
   * Scroll the selected list item into the view.
   * @returns {void}
   */
  scrollToSelectedItem () {
    const selectedLayerElement = document.querySelector('.data-selector__content a.active');
    selectedLayerElement.scrollIntoView({ block: 'center' });
  }
};